<app-main-layout>
  <div class="bg-neutral-50 min-h-screen">
  <div class="py-6 px-6 max-w-3xl mx-auto">

    <div class="mb-5">
      <h1 class="text-2xl font-bold text-neutral-800">My Profile</h1>
      <p class="text-sm text-neutral-500 mt-0.5">Manage your account details and password</p>
    </div>

    @if (loading) {
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center text-neutral-500">
        Loading your profile...
      </div>
    } @else {

      <!-- Account details -->
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 mb-5">
        <h2 class="text-base font-bold text-neutral-800 mb-4">Account Details</h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide">Email</label>
            <div class="mt-1 p-2.5 rounded-lg border border-neutral-200 bg-neutral-100 w-full text-sm text-neutral-500">
              {{ useremail }}
            </div>
          </div>
          @if (!canEditRole) {
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide">Role</label>
              <div class="mt-1 p-2.5 rounded-lg border border-neutral-200 bg-neutral-100 w-full text-sm text-neutral-500">
                {{ userrole }}
              </div>
            </div>
          }
        </div>

        @if (canEditRole) {
          <div class="mb-4">
            <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide">Role</label>
            <div class="mt-1 flex flex-row flex-wrap gap-x-5 gap-y-2 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50">
              @for (role of allRoles; track role) {
                <label class="flex items-center gap-1.5 text-sm text-neutral-700 cursor-pointer">
                  <input
                    type="checkbox"
                    class="accent-amber-500 h-4 w-4 cursor-pointer"
                    [checked]="isRoleSelected(role)"
                    (change)="toggleRole(role, $any($event.target).checked)"
                  />
                  {{ role }}
                </label>
              }
            </div>
            <p class="mt-1 text-xs text-amber-600">Only visible to this account - everyone else's role is read-only here.</p>
          </div>
        }

        <form [formGroup]="profileForm" (ngSubmit)="onSaveProfile()">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="firstname">First Name</label>
              <input
                id="firstname"
                formControlName="firstname"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              @if (p['firstname'].invalid && p['firstname'].touched) {
                <div class="mt-1 text-xs text-red-500">First name is required</div>
              }
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="lastname">Last Name</label>
              <input
                id="lastname"
                formControlName="lastname"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              @if (p['lastname'].invalid && p['lastname'].touched) {
                <div class="mt-1 text-xs text-red-500">Last name is required</div>
              }
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="displayname">Display Name</label>
              <input
                id="displayname"
                formControlName="displayname"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="phone">Phone</label>
              <input
                id="phone"
                formControlName="phone"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="vatnumber">VAT Number</label>
              <input
                id="vatnumber"
                formControlName="vatnumber"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
          </div>

          <div class="mt-5 pt-4 border-t border-neutral-100 flex justify-end">
            <button mat-raised-button color="primary" type="submit" [disabled]="savingProfile">
              {{ savingProfile ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Change password -->
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
        <h2 class="text-base font-bold text-neutral-800 mb-1">Change Password</h2>
        <p class="text-xs text-neutral-500 mb-4">Use a strong password you don't use elsewhere</p>

        <form [formGroup]="passwordForm" (ngSubmit)="onChangePassword()">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="oldPwd">Current Password</label>
              <input
                id="oldPwd"
                type="password"
                autocomplete="current-password"
                formControlName="oldPwd"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              @if (pwd['oldPwd'].invalid && pwd['oldPwd'].touched) {
                <div class="mt-1 text-xs text-red-500">Current password is required</div>
              }
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="newPwd">New Password</label>
              <input
                id="newPwd"
                type="password"
                autocomplete="new-password"
                formControlName="newPwd"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              @if (pwd['newPwd'].invalid && pwd['newPwd'].touched) {
                <div class="mt-1 text-xs text-red-500">At least 8 characters</div>
              }
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide" for="confirmPwd">Confirm New Password</label>
              <input
                id="confirmPwd"
                type="password"
                autocomplete="new-password"
                formControlName="confirmPwd"
                class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              @if (pwd['confirmPwd'].invalid && pwd['confirmPwd'].touched) {
                <div class="mt-1 text-xs text-red-500">Please confirm your new password</div>
              }
            </div>
          </div>

          <div class="mt-5 pt-4 border-t border-neutral-100 flex justify-end">
            <button mat-raised-button color="accent" type="submit" [disabled]="savingPassword">
              {{ savingPassword ? 'Updating...' : 'Update Password' }}
            </button>
          </div>
        </form>
      </div>

    }

  </div>
  </div>
</app-main-layout>
